import React, { useState } from 'react';

import { MdLanguage, MdExpandMore } from 'react-icons/md';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import './style.css';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
    { code: 'rus', name: 'Русский', flag: '🇷🇺' },
    { code: 'ua', name: 'Українська', flag: '🇺🇦' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const location = useLocation();
  const navigate = useNavigate();

  // Route mappings for each language
  /* const routeMap = {
    pl: {
      '/': '/',
      '/dla-pracownika': '/dla-pracownika',
      '/dla-pracodawcy': '/dla-pracodawcy',
      '/oferty-pracy': '/oferty-pracy',
      '/nasz-zespol': '/nasz-zespol',
      '/partnerzy': '/partnerzy',
      '/kontakt': '/kontakt'
    },
    rus: {
      '/': '/rus',
      '/dla-pracownika': '/rus/dla-sotrudnika',
      '/dla-pracodawcy': '/rus/dla-rabotodatelya',
      '/oferty-pracy': '/rus/vakansii',
      '/nasz-zespol': '/rus/nasha-komanda',
      '/partnerzy': '/rus/partnery',
      '/kontakt': '/rus/kontakt'
    },
    ua: {
      '/': '/ua',
      '/dla-pracownika': '/ua/dlya-pratsivnika',
      '/dla-pracodawcy': '/ua/dlya-robotodavtsya',
      '/oferty-pracy': '/ua/vakansii',
      '/nasz-zespol': '/ua/nasha-komanda',
      '/partnerzy': '/ua/partneri',
      '/kontakt': '/ua/kontakt'
    },
    en: {
      '/': '/en',
      '/dla-pracownika': '/en/for-workers',
      '/dla-pracodawcy': '/en/for-employers',
      '/oferty-pracy': '/en/job-offers',
      '/nasz-zespol': '/en/our-team',
      '/partnerzy': '/en/partners',
      '/kontakt': '/en/contact'
    }
  }; */

  const getCurrentPageType = () => {
    const pathname = location.pathname;
    
    // Create reverse lookup for page types
    const pageTypeMap = {
      // Home pages
      '/': 'home',
      '/rus': 'home',
      '/ua': 'home', 
      '/en': 'home',
      
      // Worker pages
      '/dla-pracownika': 'forWorkers',
      '/rus/dla-sotrudnika': 'forWorkers',
      '/ua/dlya-pratsivnika': 'forWorkers',
      '/en/for-workers': 'forWorkers',
      
      // Employer pages
      '/dla-pracodawcy': 'forEmployers',
      '/rus/dla-rabotodatelya': 'forEmployers',
      '/ua/dlya-robotodavtsya': 'forEmployers',
      '/en/for-employers': 'forEmployers',
      
      // Job offers
      '/oferty-pracy': 'jobOffers',
      '/rus/vakansii': 'jobOffers',
      '/ua/vakansii': 'jobOffers',
      '/en/job-offers': 'jobOffers',
      
      // Team pages
      '/nasz-zespol': 'team',
      '/rus/nasha-komanda': 'team',
      '/ua/nasha-komanda': 'team',
      '/en/our-team': 'team',
      
      // Partners
      '/partnerzy': 'partners',
      '/rus/partnery': 'partners',
      '/ua/partneri': 'partners',
      '/en/partners': 'partners',
      
      // Contact
      '/kontakt': 'contact',
      '/rus/kontakt': 'contact',
      '/ua/kontakt': 'contact',
      '/en/contact': 'contact'
    };
    
    // Handle job detail pages
    if (pathname.includes('/oferty-pracy/') || pathname.includes('/vakansii/') || pathname.includes('/job-offers/')) {
      return 'jobDetail';
    }
    
    return pageTypeMap[pathname] || 'home';
  };

  const getRouteForLanguage = (targetLang) => {
    const pageType = getCurrentPageType();
    
    // Map page types to routes for each language
    const pageRoutes = {
      home: {
        pl: '/',
        rus: '/rus',
        ua: '/ua',
        en: '/en'
      },
      forWorkers: {
        pl: '/dla-pracownika',
        rus: '/rus/dla-sotrudnika',
        ua: '/ua/dlya-pratsivnika',
        en: '/en/for-workers'
      },
      forEmployers: {
        pl: '/dla-pracodawcy',
        rus: '/rus/dla-rabotodatelya',
        ua: '/ua/dlya-robotodavtsya',
        en: '/en/for-employers'
      },
      jobOffers: {
        pl: '/oferty-pracy',
        rus: '/rus/vakansii',
        ua: '/ua/vakansii',
        en: '/en/job-offers'
      },
      team: {
        pl: '/nasz-zespol',
        rus: '/rus/nasha-komanda',
        ua: '/ua/nasha-komanda',
        en: '/en/our-team'
      },
      partners: {
        pl: '/partnerzy',
        rus: '/rus/partnery',
        ua: '/ua/partneri',
        en: '/en/partners'
      },
      contact: {
        pl: '/kontakt',
        rus: '/rus/kontakt',
        ua: '/ua/kontakt',
        en: '/en/contact'
      }
    };
    
    // Handle job detail pages
    if (pageType === 'jobDetail') {
      const pathname = location.pathname;
      let jobId = '';
      
      if (pathname.includes('/oferty-pracy/')) {
        jobId = pathname.split('/oferty-pracy/')[1];
      } else if (pathname.includes('/vakansii/')) {
        jobId = pathname.split('/vakansii/')[1];
      } else if (pathname.includes('/job-offers/')) {
        jobId = pathname.split('/job-offers/')[1];
      }
      
      const jobDetailRoutes = {
        pl: `/oferty-pracy/${jobId}`,
        rus: `/rus/vakansii/${jobId}`,
        ua: `/ua/vakansii/${jobId}`,
        en: `/en/job-offers/${jobId}`
      };
      
      return jobDetailRoutes[targetLang];
    }
    
    return pageRoutes[pageType]?.[targetLang] || pageRoutes.home[targetLang];
  };

  const handleLanguageChange = (languageCode) => {
    const newRoute = getRouteForLanguage(languageCode);
    i18n.changeLanguage(languageCode);
    navigate(newRoute);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className='LanguageSwitcher'>
      <button
        className='LanguageSwitcher__button'
        onClick={toggleDropdown}
        aria-label='Language switcher'
      >
        <MdLanguage className='LanguageSwitcher__icon' />
        <span className='LanguageSwitcher__current'>
          <span className='LanguageSwitcher__flag'>{currentLanguage.flag}</span>
          <span className='LanguageSwitcher__name'>{currentLanguage.name}</span>
        </span>
        <MdExpandMore
          className={`LanguageSwitcher__arrow ${isOpen ? 'LanguageSwitcher__arrow--open' : ''}`}
        />
      </button>

      {isOpen && (
        <div className='LanguageSwitcher__dropdown'>
          <div className='LanguageSwitcher__list'>
            {languages.map((language) => (
              <button
                key={language.code}
                className={`LanguageSwitcher__item ${
                  i18n.language === language.code
                    ? 'LanguageSwitcher__item--active'
                    : ''
                }`}
                data-lang={language.code}
                onClick={() => handleLanguageChange(language.code)}
              >
                <span className='LanguageSwitcher__flag'>{language.flag}</span>
                <span className='LanguageSwitcher__name'>{language.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className='LanguageSwitcher__overlay'
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default LanguageSwitcher;
